import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { rotatedVisualAabbOfShape } from '@/lib/shapes/shape-coordinates'
import { normalizeRotationDegrees, shortestDeltaDegrees } from '@/lib/transforms/geometry'
import { isShapeGeometryGestureBlocked } from '@/lib/groups/element-edit-policy'
import type { MultiSelectChrome } from '@/lib/transforms/types/multi-select-chrome'
import type { MultiSelectShapeGeomSnap } from '@/lib/transforms/types/multi-select-shape-geom-snap'

/** Match object panel / shape state: degrees in [0, 360), integer. */
export function roundRotationDegrees(degrees: number): number {
  return normalizeRotationDegrees(Math.round(normalizeRotationDegrees(degrees)))
}

/**
 * Canvas-space union of each shape's rotated VISUAL AABB — the same box `rotatedVisualAabbOfShape`
 * gives a single selected shape's own chrome (`selectionChromeFrameOfShape`,
 * `selectionChromeUnionOfShapes`), so a multi-selection's box hugs an inset shape's (triangle,
 * star, custom path) real outline the same way a single one already does, instead of standing off
 * its edges. Pure and synchronous — no DOM-measured `ShapeBounds` involved, unlike the
 * `canvasAabbOfRotatedPathBounds` this replaced, which is why the callers below no longer need to
 * wait for a bounds measurement to arrive before the box is right (see the deleted
 * bounds-refresh effect this made obsolete).
 */
export function unionCanvasAabbForShapes(
  shapes: ShapeData[]
): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (shapes.length === 0) return null

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const s of shapes) {
    const abb = rotatedVisualAabbOfShape(s)

    minX = Math.min(minX, abb.x)
    minY = Math.min(minY, abb.y)
    maxX = Math.max(maxX, abb.x + abb.width)
    maxY = Math.max(maxY, abb.y + abb.height)
  }

  return { minX, minY, maxX, maxY }
}

export function computeMultiSelectChrome(
  ids: string[],
  shapesList: ShapeData[]
): MultiSelectChrome | null {
  const selected = shapesList.filter(
    (s) => ids.includes(s.id) && s.locked !== true && s.hidden !== true
  )

  if (selected.length < 2) return null

  const union = unionCanvasAabbForShapes(selected)

  if (!union) return null

  const width = union.maxX - union.minX
  const height = union.maxY - union.minY

  return {
    pivotX: union.minX + width / 2,
    pivotY: union.minY + height / 2,
    width,
    height,
    rotationDeg: 0,
  }
}

export function getMultiSelectableIds(ids: string[], shapesList: ShapeData[]): string[] {
  const shapesById = new Map(shapesList.map((shape) => [shape.id, shape]))

  return shapesList
    .filter((shape) => {
      if (!ids.includes(shape.id) || shape.hidden === true || shape.locked === true) {
        return false
      }

      return !isShapeGeometryGestureBlocked(shape, shapesById)
    })
    .map((shape) => shape.id)
}

export function buildMultiSelectShapeSnap(
  selectableIds: string[],
  shapesList: ShapeData[]
): Record<string, MultiSelectShapeGeomSnap> {
  const m: Record<string, MultiSelectShapeGeomSnap> = {}

  for (const id of selectableIds) {
    const s = shapesList.find((sh) => sh.id === id)

    if (!s) continue

    m[id] = {
      x: s.x,
      y: s.y,
      rotation: s.rotation ?? 0,
      width: s.width ?? 0,
      height: s.height ?? 0,
    }
  }

  return m
}

/** Same (dx, dy) on every selected shape, rotation/size unchanged — e.g. object-panel X/Y nudge. */
export function rigidTranslationForMultiSelect(
  selectableIds: string[],
  prevSnap: Record<string, MultiSelectShapeGeomSnap> | null,
  shapesList: ShapeData[]
): { dx: number; dy: number } | null {
  if (!prevSnap || selectableIds.length === 0) return null

  const shapesById = new Map(shapesList.map((shape) => [shape.id, shape]))

  for (const id of selectableIds) {
    if (prevSnap[id] === undefined) return null
  }

  if (Object.keys(prevSnap).length !== selectableIds.length) return null

  let dx: number | undefined
  let dy: number | undefined

  for (const id of selectableIds) {
    const p = prevSnap[id]
    const s = shapesList.find((sh) => sh.id === id)

    if (!p || !s || s.hidden === true) return null
    if (isShapeGeometryGestureBlocked(s, shapesById)) return null

    const dxi = s.x - p.x
    const dyi = s.y - p.y

    if (
      Math.abs(shortestDeltaDegrees(p.rotation, s.rotation ?? 0)) > 1e-2 ||
      Math.abs((s.width ?? 0) - p.width) > 1e-3 ||
      Math.abs((s.height ?? 0) - p.height) > 1e-3
    ) {
      return null
    }

    if (dx === undefined) {
      dx = dxi
      dy = dyi
    } else if (dy !== undefined && (Math.abs(dx - dxi) > 1e-3 || Math.abs(dy - dyi) > 1e-3)) {
      return null
    }
  }

  return dx !== undefined && dy !== undefined ? { dx, dy } : null
}

export function chromeNearlyEqual(
  a: MultiSelectChrome | null,
  b: MultiSelectChrome | null
): boolean {
  if (a === b) return true
  if (!a || !b) return false

  return (
    Math.abs(a.pivotX - b.pivotX) < 1e-3 &&
    Math.abs(a.pivotY - b.pivotY) < 1e-3 &&
    Math.abs(a.width - b.width) < 1e-3 &&
    Math.abs(a.height - b.height) < 1e-3 &&
    Math.abs(a.rotationDeg - b.rotationDeg) < 0.5
  )
}
